import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  TextField,
  Typography,
  Box,
  Paper,
  Grid,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import comms from "./comms";
import VehicleSelection from "./VehicleSelection";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import packageJson from "../../package.json";

function Login({ onLoginSuccess }) {
  // Persistent data (survives reload)
  const [fleetCode, setFleetCode] = useState(
    localStorage.getItem("fleetCode") || ""
  );
  const [driverNumber, setDriverNumber] = useState(
    localStorage.getItem("driverNumber") || ""
  );
  const [pin, setPin] = useState(localStorage.getItem("pin") || "");

  // UI state
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState("loading"); // loading, fleetCode, vehicleSelection, login

  // Fleet and auth data
  const [fleetSettings, setFleetSettings] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);

  // #1 Restartable shift: holds { shift, result, settings } when the driver
  // closed a shift earlier today whose route is still scheduled into the future.
  const [restartable, setRestartable] = useState(null);
  
  // Use ref to prevent infinite loop - tracks if auto-login is currently running
  const isAutoLoginInProgress = useRef(false);
  
  // Define all callback functions first
  const connectAndProceed = useCallback(async (authResult, targetScreen, settings) => {
    const settingsToUse = settings || fleetSettings;
    
    try {
      setLoading(true);
      const socket = await comms.connectSocket(settingsToUse.fleetUrl);
      onLoginSuccess(socket, settingsToUse, targetScreen, authResult);
    } catch (error) {
      console.error("Socket connection error:", error);
      
      // Even if socket fails, proceed with offline mode if we have the data
      if (authResult && settingsToUse) {
        console.log("Socket connection failed, proceeding in offline mode");
        setError("Working in offline mode - real-time features unavailable");
        onLoginSuccess(null, settingsToUse, targetScreen, authResult);
      } else {
        setError("Failed to connect to server");
        setLoading(false);
      }
    }
  }, [fleetSettings, onLoginSuccess]);

  // Core routing logic: decide dashboard vs dispatch and proceed.
  const proceedAuth = useCallback((authResult, settings) => {
    const settingsToUse = settings || fleetSettings;

    // Dashboard mode - redirect directly (skip dispatch flow entirely)
    if (authResult.defaultScreen === "dashboard") {
      connectAndProceed(authResult, "dashboard", settingsToUse);
      return;
    }

    // Dispatch mode - handle vehicle assignment and dispatch flow
    const dispatchData = authResult.dispatchData;
    let vehicleId, vehicleData;

    // Determine vehicle data source based on fleet settings
    if (settingsToUse?.vehicleSelection) {
      // Manual vehicle selection - use selected vehicle
      vehicleId = selectedVehicle?.Id || selectedVehicle?.id;
      vehicleData = selectedVehicle;
    } else {
      // Automatic vehicle assignment from shift/route
      vehicleId = dispatchData?.vehicleId;
      vehicleData = dispatchData?.vehicleObject;

      // Store the server-assigned vehicle for consistency
      if (vehicleData && vehicleId) {
        setSelectedVehicle(vehicleData);
        localStorage.setItem("selectedVehicle", JSON.stringify(vehicleData));
        localStorage.setItem("selectedVehicleId", vehicleId);
      }
    }

    let targetScreen = "dispatch"; // Always route to dispatch for dispatch mode

    connectAndProceed(authResult, targetScreen, settingsToUse);
  }, [fleetSettings, selectedVehicle, connectAndProceed]);

  const handleAuthSuccess = useCallback((authResult, settings) => {
    // #1 Restartable shift: if the driver closed a shift earlier today whose
    // route is still scheduled, prompt to resume before normal routing.
    if (authResult?.restartableShift) {
      setRestartable({
        shift: authResult.restartableShift,
        result: authResult,
        settings: settings || fleetSettings,
      });
      setLoading(false);
      return;
    }
    proceedAuth(authResult, settings);
  }, [fleetSettings, proceedAuth]);

  // #1 Resume the previously closed shift, then go straight to dispatch.
  const handleResumeShift = useCallback(async () => {
    if (!restartable) return;
    const { shift, result, settings } = restartable;
    try {
      setLoading(true);
      await comms.resumeShift(shift.routeId, shift.vehicleId, shift.driverId);
      setRestartable(null);
      // Route straight into LiveDispatch/Itinerary (skip route/vehicle picker).
      connectAndProceed(result, "dispatch", settings || fleetSettings);
    } catch (e) {
      console.error("Resume shift failed:", e);
      setError("Could not resume shift. Please start fresh.");
      setLoading(false);
    }
  }, [restartable, connectAndProceed, fleetSettings]);

  // #1 Decline resume — continue with the normal post-auth flow.
  const handleStartFresh = useCallback(() => {
    if (!restartable) return;
    const { result, settings } = restartable;
    setRestartable(null);
    proceedAuth(result, settings);
  }, [restartable, proceedAuth]);
  
  // Initialize and handle auto-login on component mount
  useEffect(() => {
    const initializeLogin = async () => {
      // Prevent multiple simultaneous auto-login attempts
      if (isAutoLoginInProgress.current) {
        console.log('Auto-login already in progress, skipping');
        return;
      }
      
      isAutoLoginInProgress.current = true;
      
      // Check if we have stored credentials
      const storedFleetCode = localStorage.getItem("fleetCode");
      const storedDriverNumber = localStorage.getItem("driverNumber");
      const storedPin = localStorage.getItem("pin");
      const storedFleetSettings = localStorage.getItem("fleetSettings");
      const storedVehicle = localStorage.getItem("selectedVehicle");
      
      // No fleet code - go to fleet code entry
      if (!storedFleetCode) {
        setCurrentStep("fleetCode");
        setAutoLoginAttempted(true);
        isAutoLoginInProgress.current = false;
        return;
      }
      
      // Have fleet code but missing credentials - determine which step to show
      if (!storedDriverNumber || !storedPin) {
        // Try to load fleet settings to check if vehicle selection is needed
        try {
          let settings = storedFleetSettings ? JSON.parse(storedFleetSettings) : null;
          
          // If no cached settings, try to fetch them
          if (!settings) {
            try {
              settings = await comms.getSettings(storedFleetCode);
              setFleetSettings(settings);
            } catch (e) {
              console.log("Could not fetch settings, going to login");
              setCurrentStep("login");
              setAutoLoginAttempted(true);
              isAutoLoginInProgress.current = false;
              return;
            }
          } else {
            setFleetSettings(settings);
            setFleetCode(storedFleetCode);
          }
          
          // Check if vehicle selection is required and not yet selected
          if (settings.vehicleSelection && !storedVehicle) {
            setCurrentStep("vehicleSelection");
          } else {
            // Have fleet code (and vehicle if needed), just need login
            if (storedVehicle) {
              try {
                setSelectedVehicle(JSON.parse(storedVehicle));
              } catch (e) {}
            }
            setCurrentStep("login");
          }
        } catch (e) {
          console.error("Error checking settings:", e);
          setCurrentStep("login");
        }
        
        setAutoLoginAttempted(true);
        isAutoLoginInProgress.current = false;
        return;
      }
    
      // We have credentials - attempt auto-login
      try {
        setLoading(true);
        
        // Refresh fleet settings (with fallback to cached)
        console.log("Refreshing fleet settings for:", storedFleetCode);
        const settings = await comms.getSettings(storedFleetCode);
        setFleetSettings(settings);
        
        // Check vehicle selection requirements
        let vehicleToUse = null;
        if (settings.vehicleSelection) {
          const storedVehicle = localStorage.getItem("selectedVehicle");
          if (storedVehicle) {
            try {
              vehicleToUse = JSON.parse(storedVehicle);
              setSelectedVehicle(vehicleToUse);
            } catch (e) {
              console.error("Error parsing stored vehicle:", e);
            }
          }
          
          // Vehicle selection required but no vehicle selected
          if (!vehicleToUse) {
            setCurrentStep("vehicleSelection");
            setLoading(false);
            return;
          }
        }
        
        // Attempt authorization with stored credentials
        const vehicleId = vehicleToUse ? (vehicleToUse.Id || vehicleToUse.id) : null;
        console.log("Attempting auto-login for driver:", storedDriverNumber);
        
        const result = await comms.authorize(storedDriverNumber, storedPin, null, vehicleId);
        
        // Success - proceed to connect and login
        console.log("Auto-login successful");
        handleAuthSuccess(result, settings);
        
      } catch (error) {
        console.error("Auto-login failed:", error);
        
        // Check if this is an explicit auth failure (401 with Invalid credentials)
        if (error.message === "Invalid credentials") {
          // Clear credentials and show login screen
          localStorage.removeItem("driverNumber");
          localStorage.removeItem("pin");
          setDriverNumber("");
          setPin("");
          setError("Session expired. Please login again.");
          setCurrentStep("login");
        } else {
          // Network error or other issue - try to use offline mode
          console.log("Network error during auto-login, attempting offline mode");
          try {
            // Try to get cached auth data
            const offlineResult = await comms.authorize(storedDriverNumber, storedPin, null, null, true);
            const offlineSettings = await comms.getSettings(storedFleetCode, true);
            
            setFleetSettings(offlineSettings);
            
            console.log("Using cached data (offline mode)");
            setError("Using offline mode - limited functionality");
            handleAuthSuccess(offlineResult, offlineSettings);
          } catch (offlineError) {
            console.error("Offline mode failed:", offlineError);
            setError("Connection error. Please check your network.");
            setCurrentStep("login");
          }
        }
      } finally {
        setLoading(false);
        setAutoLoginAttempted(true);
        isAutoLoginInProgress.current = false;
      }
    };
    
    if (!autoLoginAttempted) {
      initializeLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoginAttempted]); // handleAuthSuccess intentionally excluded to prevent infinite loop
  
  // Monitor fleet code changes (manual entry)
  useEffect(() => {
    if (!autoLoginAttempted || currentStep === "loading") {
      return;
    }
    
    // Check if we already have fleet settings
    const storedFleetSettings = localStorage.getItem("fleetSettings");
    if (storedFleetSettings && fleetCode) {
      try {
        const settings = JSON.parse(storedFleetSettings);
        setFleetSettings(settings);
        
        // Check if vehicle selection is required by fleet settings
        if (settings.vehicleSelection) {
          const storedVehicle = localStorage.getItem("selectedVehicle");
          if (storedVehicle) {
            try {
              const vehicle = JSON.parse(storedVehicle);
              setSelectedVehicle(vehicle);
            } catch (e) {
              console.error("Error parsing stored vehicle:", e);
            }
          }
        }
      } catch (error) {
        console.error("Error parsing stored fleet settings:", error);
        localStorage.removeItem("fleetSettings");
      }
    }
  }, [fleetCode, autoLoginAttempted, currentStep]);

  const handleFleetCodeSubmit = async () => {
    if (!fleetCode) {
      setError("Please enter a fleet code");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Always fetch fresh settings on login
      const settings = await comms.getSettings(fleetCode);
      setFleetSettings(settings);
      localStorage.setItem("fleetCode", fleetCode);
      
      // Check if vehicle selection is required by fleet settings
      if (settings.vehicleSelection) {
        setCurrentStep("vehicleSelection");
      } else {
        setCurrentStep("login");
      }
    } catch (error) {
      console.error("Fleet settings error:", error);
      setError("Invalid fleet code or connection error");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!driverNumber || !pin) {
      setError("Please enter both driver number and PIN");
      return;
    }

    // Only require vehicle selection if fleet settings require it
    if (fleetSettings?.vehicleSelection && !selectedVehicle) {
      setError("Please select a vehicle first");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Refresh fleet settings on every login
      const settings = await comms.getSettings(fleetCode);
      setFleetSettings(settings);

      // Pass vehicleId if vehicle selection is required and vehicle is selected
      const vehicleId = settings?.vehicleSelection && selectedVehicle ? 
        (selectedVehicle.Id || selectedVehicle.id) : null;
      
      const result = await comms.authorize(driverNumber, pin, null, vehicleId);

      // Store credentials for future auto-login
      localStorage.setItem("driverNumber", driverNumber);
      localStorage.setItem("pin", pin);

      // FEB 2026: Clear any stale persisted route data before proceeding
      // This prevents stale route IDs from yesterday being used on a new day
      comms.clearPersistedRouteData();

      handleAuthSuccess(result, settings);
    } catch (error) {
      console.error("Login error:", error);
      
      // Check if this is an explicit auth failure
      if (error.message === "Invalid credentials") {
        setError("Invalid driver number or PIN");
      } else {
        // Network error - try offline mode
        try {
          const offlineResult = await comms.authorize(driverNumber, pin, null, null, true);
          const offlineSettings = await comms.getSettings(fleetCode, true);
          
          setFleetSettings(offlineSettings);
          setError("Using offline mode - limited functionality");
          
          handleAuthSuccess(offlineResult, offlineSettings);
        } catch (offlineError) {
          setError(error.message);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleSelected = (vehicle) => {
    setSelectedVehicle(vehicle);
    localStorage.setItem("selectedVehicle", JSON.stringify(vehicle));
    localStorage.setItem("selectedVehicleId", vehicle.Id);
    
    // After vehicle selection, proceed to login
    setCurrentStep("login");
  };

  const goToVehicleSelection = () => {
    setCurrentStep("vehicleSelection");
  };

  const resetFleetCode = () => {
    localStorage.removeItem("fleetCode");
    localStorage.removeItem("fleetSettings");
    localStorage.removeItem("selectedVehicle");
    localStorage.removeItem("selectedVehicleId");
    // Another fleet is another dispatch server: a status carried over from the old
    // one describes a vehicle that does not exist here.
    comms.clearCurrentStatus();
    setFleetCode("");
    setFleetSettings(null);
    setSelectedVehicle(null);
    setCurrentStep("fleetCode");
  };

  const renderFleetCodeStep = () => (
    <Paper elevation={3} sx={{ padding: 3, maxWidth: 400, margin: "auto" }}>
      <Typography variant="h5" gutterBottom>
        Enter Fleet Code
      </Typography>
      <TextField
        fullWidth
        label="Fleet Code"
        value={fleetCode}
        onChange={(e) => setFleetCode(e.target.value.toUpperCase())}
        margin="normal"
        autoFocus
        onKeyPress={(e) => e.key === "Enter" && handleFleetCodeSubmit()}
      />
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Button
        fullWidth
        variant="contained"
        onClick={handleFleetCodeSubmit}
        disabled={loading || !fleetCode}
        sx={{ mt: 2 }}
      >
        {loading ? "Connecting..." : "Connect"}
      </Button>
    </Paper>
  );

  const renderLoginStep = () => (
    <>
      <Paper elevation={3} sx={{ padding: 3, maxWidth: 400, margin: "auto" }}>
        <Box display="flex" justifyContent="center" alignItems="center">
          <Typography variant="h5" gutterBottom>
            Driver Login
          </Typography>
        </Box>
        
        {fleetSettings?.vehicleSelection && (
          <Typography 
            variant="body2" 
            color="grey.500" 
            gutterBottom 
            onClick={() => setCurrentStep("vehicleSelection")}
          >
            {selectedVehicle ? 
              `${selectedVehicle.Name || selectedVehicle.name ? ` ${selectedVehicle.Name || selectedVehicle.name}` : ''}` : 
              'Click to Select Vehicle'}
          </Typography>
        )}
        
        {!fleetSettings?.vehicleSelection && (
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Vehicle assigned by shift/route
          </Typography>
        )}

        <TextField
          fullWidth
          label="Driver Number"
          value={driverNumber}
          onChange={(e) => setDriverNumber(e.target.value)}
          margin="normal"
          autoFocus={!driverNumber}
        />
        
        <TextField
          fullWidth
          label="PIN"
          type="password"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          margin="normal"
          autoFocus={!!driverNumber}
          onKeyPress={(e) => e.key === "Enter" && handleLogin()}
        />
        
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        
        <Button
          fullWidth
          variant="contained"
          onClick={handleLogin}
          disabled={loading || !driverNumber || !pin || (fleetSettings?.vehicleSelection && !selectedVehicle)}
          sx={{ mt: 2 }}
        >
          {loading ? "Logging in..." : "Login"}
        </Button>
      </Paper>

      {/* Settings button - positioned under the Paper */}
      <Box sx={{ maxWidth: 400, margin: "auto", mt: 1 }}>
        <IconButton
          onClick={goToVehicleSelection}
          sx={{
            backgroundColor: "transparent",
            color: "grey.400",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
              color: "grey.600",
            },
            boxShadow: "none",
          }}
        >
          <SettingsOutlinedIcon />
        </IconButton>
      </Box>
    </>
  );

  const renderVehicleSelection = () => (
    <VehicleSelection
      fleetSettings={fleetSettings}
      onVehicleSelected={handleVehicleSelected}
      onBack={() => {
        // Navigate back to login when coming from login screen
        setCurrentStep("login");
      }}
      onChangeFleetCode={resetFleetCode}
    />
  );



  if (loading && currentStep === "loading") {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="var(--app-viewport-height)">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      style={{ minHeight: "var(--app-viewport-height)", padding: 20, position: "relative" }}
    >
      <Grid item xs={12}>
        {currentStep === "fleetCode" && renderFleetCodeStep()}
        {currentStep === "login" && renderLoginStep()}
        {currentStep === "vehicleSelection" && renderVehicleSelection()}
        {currentStep === "loading" && (
          <Box display="flex" justifyContent="center" alignItems="center">
            <Typography>Initializing...</Typography>
          </Box>
        )}
      </Grid>
      
      {/* #1 Resume shift prompt */}
      <Dialog open={!!restartable} onClose={handleStartFresh}>
        <DialogTitle>Resume your shift?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {restartable?.shift?.routeName
              ? `You have an active route "${restartable.shift.routeName}"`
              : "You have an active route"}
            {restartable?.shift?.scheduledEnd
              ? ` scheduled until ${(() => {
                  const d = new Date(restartable.shift.scheduledEnd);
                  return isNaN(d.getTime()) ? restartable.shift.scheduledEnd : d.toLocaleString();
                })()}.`
              : "."}
            {" "}Would you like to resume it?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStartFresh} color="inherit" disabled={loading}>
            Start fresh
          </Button>
          <Button onClick={handleResumeShift} variant="contained" disabled={loading} autoFocus>
            {loading ? "Resuming..." : "Resume"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version number - bottom right */}
      <Box
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          color: "grey.700",
          fontSize: "1rem",
          userSelect: "none"
        }}
      >
        v{packageJson.version}
      </Box>
    </Grid>
  );
}

export default Login;
